import React from 'react';
import { ITableDataSource } from 'models/table';
import { colors } from 'theme/colors';
import Grid from '@mui/material/Grid';
import { Typography } from '@mui/material';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useTranslations } from 'hooks/useTranslations';
import { EPermission } from 'models/permissions';
import { emptyFieldHolder } from 'components/molecules/CardList';
import { ELEAccordionsPageTypes } from 'components/organisms/LEAccordions';
import { ECustomFieldType, ICustomFieldByObjectType } from 'models/customField';
import { ILegalEntityCustomField } from 'components/organisms/LegalEntityDetails';
import { formatDate } from 'utils/date';
import { getColumnTitleFromCustomField } from 'utils/table';

interface IOrganisationLegalEntityTableCard {
  data: ITableDataSource;
  handleView?: (arg: string) => void;
  handleEdit?: (arg: string) => void;
  handleDelete?: (arg: string) => void;
  handleSelect?: (arg: string) => void;
  customFields?: ICustomFieldByObjectType[];
  shortDateFormat: string;
  type: ELEAccordionsPageTypes;
  hasDeleteLEPermission: boolean;
  hasManageLEPermission: boolean;
  licenceAuthorityIds: string[];
  selectedLanguage: string | undefined;
}

const OrganisationLegalEntityTableCard = ({
  data,
  handleSelect,
  handleView,
  handleEdit,
  handleDelete,
  customFields,
  shortDateFormat,
  type,
  hasDeleteLEPermission,
  hasManageLEPermission,
  licenceAuthorityIds,
  selectedLanguage,
}: IOrganisationLegalEntityTableCard) => {
  const { t } = useTranslations();

  const dataToSelect = data as any;

  const getCFItem = (customField: { name: string }) => {
    if (dataToSelect?.customFields?.length) {
      const cf = dataToSelect.customFields.find(
        (cField: ILegalEntityCustomField) => cField.customFieldName === customField.name,
      );
      if (cf) {
        if (cf.customFieldType === ECustomFieldType.BOOL && t) {
          if (cf.value === null) {
            return '-';
          }
          return cf.value
            ? t('customFields.value.yes.label')
            : t('customFields.value.no.label');
        }
        if (cf.customFieldType === ECustomFieldType.DATE) {
          return formatDate(cf.value, shortDateFormat);
        }
        return cf.value || '-';
      }
    }
    return '-';
  };

  const customFieldColumns = customFields
    ? customFields
        .filter((customField) => customField.visibleInSearchResults)
        .map((customField) => (
          <Grid sx={{ p: 2 }}>
            <Grid>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {getColumnTitleFromCustomField(customField, selectedLanguage)}
              </Typography>
            </Grid>
            {getCFItem(customField)}
          </Grid>
        ))
    : null;

  return (
    <Grid
      container
      sx={{
        border: `1px solid ${colors.primary25}`,
        borderRadius: '4px',
        overflow: 'hidden',
        mt: 1,
      }}
    >
      <Grid
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          backgroundColor: colors.primary100,
          px: 2,
          py: 1,
        }}
      >
        <Grid size={{ xs: 12 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {emptyFieldHolder(data.organizationName)}
          </Typography>
        </Grid>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntities.organization.accordion.legalEntityTable.displayUniqueId.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.displayUniqueId)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t(
            'legalEntities.organization.accordion.legalEntityTable.organisationCategoryUniqueId.column',
          )}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(data.organisationCategoryUniqueId)}
        </Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('legalEntities.organization.accordion.legalEntityTable.organizationType.column')}
        </Typography>
        <Typography variant="body2">
          {emptyFieldHolder(data.organizationTypeUniqueId)}
        </Typography>
      </Grid>
      <Grid sx={{ overflowWrap: 'break-word', p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntity.legalEntityTable.departmentName.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.departmentName)}</Typography>
      </Grid>
      <Grid sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
          {t('searchForLegalEntity.legalEntityTable.city.column')}
        </Typography>
        <Typography variant="body2">{emptyFieldHolder(data.city)}</Typography>
      </Grid>

      {customFieldColumns}

      <Grid
        container
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: {
            xs: `1px solid ${colors.primary25}`,
            lg: 'none',
          },
          m: {
            xs: 2,
            lg: 2,
          },
        }}
        spacing={2}
      >
        <Grid size={{ xs: 12, sm: 2 }} sx={{ mb: { xs: 1, sm: 0 } }}>
          <Typography variant="button" sx={{ textTransform: 'uppercase' }}>
            {t('searchForLegalEntity.legalEntityTable.action.column')}:
          </Typography>
        </Grid>

        <Grid container columnSpacing={1} sx={{ justifyContent: 'flex-end' }}>
          {type === ELEAccordionsPageTypes.LegalEntity ? (
            <>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                <Button
                  variant={EButtonVariants.outlined}
                  tableButton
                  label={t(
                    'legalEntities.organization.accordion.legalEntityTable.details.link',
                  )}
                  onClick={() => handleView && handleView(`${data.id}`)}
                  fullWidth
                  size={EButtonSizes.small}
                  id={`action_mobile_view_${data.id}`}
                />
              </Grid>
              {hasManageLEPermission &&
                (licenceAuthorityIds.includes(data.primaryWeaponLicencingAuthorityUniqueId) ||
                  licenceAuthorityIds.includes(
                    data.primaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                    <Button
                      variant={EButtonVariants.outlined}
                      tableButton
                      label={t(
                        'legalEntities.organization.accordion.legalEntityTable.edit.link',
                      )}
                      onClick={() => handleEdit && handleEdit(`${data.id}`)}
                      fullWidth
                      size={EButtonSizes.small}
                      id={`action_mobile_edit_${data.id}`}
                    />
                  </Grid>
                )}
              {hasDeleteLEPermission &&
                (licenceAuthorityIds.includes(data.primaryWeaponLicencingAuthorityUniqueId) ||
                  licenceAuthorityIds.includes(
                    data.primaryWeaponLicencingAuthorityUniqueId,
                  )) && (
                  <Grid size={{ xs: 12, sm: 4 }} sx={{ mb: { xs: 1, sm: 0 } }}>
                    <Button
                      variant={EButtonVariants.outlined}
                      tableButton
                      label={t(
                        'legalEntities.organization.accordion.legalEntityTable.delete.link',
                      )}
                      onClick={() => handleDelete && handleDelete(`${data.id}`)}
                      permission={[EPermission.DELETE_LE]}
                      fullWidth
                      size={EButtonSizes.small}
                      id={`action_mobile_delete_${data.id}`}
                    />
                  </Grid>
                )}
            </>
          ) : (
            <Grid size={{ xs: 12, sm: 10 }} sx={{ mb: { xs: 1, sm: 0 } }}>
              <Button
                label={t('legalEntities.organization.accordion.legalEntityTable.select.link')}
                variant={EButtonVariants.outlined}
                onClick={() =>
                  handleSelect &&
                  handleSelect(
                    type && type === ELEAccordionsPageTypes.SelectLegalEntity
                      ? dataToSelect
                      : `${data.id}`,
                  )
                }
                fullWidth
                size={EButtonSizes.small}
                id={`action_mobile_check_${data.id}`}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export { OrganisationLegalEntityTableCard };
